import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// Public page. Consumed with a one-time token that only the current Super
// Admin can generate from the /admin dashboard. No CLI, no curl, no manual
// API calls needed by the user.
export default function AdminPasswordReset() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Token mancante nel link.");
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!token) return toast.error("Token mancante.");
    if (pwd.length < 8) return toast.error("La password deve avere almeno 8 caratteri.");
    if (pwd !== pwd2) return toast.error("Le due password non coincidono.");
    setBusy(true);
    try {
      await api.post("/auth/admin/reset", { token, new_password: pwd });
      setDone(true);
      toast.success("Password aggiornata. Ora effettua il login.");
      setTimeout(() => nav("/auth?mode=signin"), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Token non valido o scaduto.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-4" data-testid="admin-reset-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center"><ShieldCheck size={18} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Super Admin</div>
            <div className="font-heading text-xl font-bold text-slate-900">{t("admin_password_reset_title")}</div>
          </div>
        </div>
        {done ? (
          <div className="p-6 text-center" data-testid="admin-reset-done">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-2" />
            <div className="text-sm text-slate-800 font-semibold">Password aggiornata correttamente.</div>
            <div className="text-xs text-slate-500 mt-1">Ti stiamo reindirizzando al login…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <div className="text-xs text-slate-500">
              Il link è monouso e scade 15 minuti dopo la generazione. Scegli una password di almeno 8 caratteri.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("admin_new_password")}</label>
              <div className="relative">
                <Input data-testid="admin-reset-pwd" type={show ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)} minLength={8} required className="rounded-full mt-1 pr-10" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" aria-label={t("show_hide")}>
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("admin_repeat_password")}</label>
              <Input data-testid="admin-reset-pwd2" type={show ? "text" : "password"} value={pwd2} onChange={(e) => setPwd2(e.target.value)} minLength={8} required className="rounded-full mt-1" />
            </div>
            <Button
              type="submit"
              data-testid="admin-reset-submit"
              disabled={busy || !token || pwd.length < 8 || pwd !== pwd2}
              className="w-full h-11 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white disabled:opacity-60"
            >
              {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null} Imposta nuova password
            </Button>
            <div className="text-[11px] text-slate-500 text-center pt-2">
              <Link to="/auth?mode=signin" className="underline">{t("admin_back_to_login")}</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
